"use client";

import { useMemo } from "react";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import type { PricePoint } from "@/lib/data/types";
import { stepRetroactiveScoreOntoDates, type RetroactiveScorePoint } from "@/lib/scoring/retroactive-score";
import { computeYearTicks, downsample, pricePrecisionDigits, withTimeField } from "@/lib/price-chart-utils";

const GOLD = "#C6A15B";
const NAVY = "#16265C";

function formatTooltipDate(value: string | number): string {
  return new Date(value).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" });
}

/**
 * Score-Zeitmaschine-Chart (backlog.md -> D3): Kurs (linke Achse) + rückwirkend
 * berechneter Score (rechte Achse, fest 0-100) übereinandergelegt. `points` kommt aus
 * `computeRetroactiveScoreSeries()` (server-seitig in score-section.tsx berechnet, hier
 * nur gerendert), wird per `stepRetroactiveScoreOntoDates()` auf dieselben Kurs-Stichtage
 * gestuft wie die Preislinie - gleiches Prinzip wie die bestehende
 * `DailyMultiplesChart`/`computeDailyMultiple()`-Kombination im Bewertung-Tab.
 */
export function ScoreTimeMachineChart({
  points,
  priceHistory,
  currency = "USD",
}: {
  points: RetroactiveScorePoint[];
  priceHistory: PricePoint[];
  currency?: string;
}) {
  const priceDigits = useMemo(
    () => pricePrecisionDigits(priceHistory[priceHistory.length - 1]?.close),
    [priceHistory],
  );
  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
        minimumFractionDigits: priceDigits,
        maximumFractionDigits: priceDigits,
      }),
    [currency, priceDigits],
  );

  const chartData = useMemo(() => {
    const sortedPrice = [...priceHistory].sort((a, b) => a.date.localeCompare(b.date));
    const scoreByDate = new Map(
      stepRetroactiveScoreOntoDates(points, sortedPrice.map((p) => p.date)).map((s) => [s.date, s.score]),
    );
    const merged = sortedPrice.map((p) => ({ date: p.date, close: p.close, score: scoreByDate.get(p.date) }));
    return withTimeField(downsample(merged, 1000, (p) => p.close));
  }, [priceHistory, points]);

  const axisTicks = useMemo(() => computeYearTicks(chartData, 8).map((d) => Date.parse(d)), [chartData]);

  if (chartData.length === 0) return null;

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="scoreTimeMachinePriceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={NAVY} stopOpacity={0.18} />
              <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={NAVY} strokeOpacity={0.12} strokeDasharray="2 4" />
          <XAxis
            dataKey="time"
            type="number"
            domain={["dataMin", "dataMax"]}
            scale="time"
            tickFormatter={(v: number) => String(new Date(v).getUTCFullYear())}
            ticks={axisTicks}
            interval={0}
            tick={{ fontSize: 10, fill: "#6B7280" }}
            axisLine={{ stroke: "#ECEAE3" }}
            tickLine={false}
          />
          <YAxis
            yAxisId="price"
            domain={["auto", "auto"]}
            tick={{ fontSize: 10, fill: "#6B7280" }}
            tickFormatter={(v: number) => priceFormatter.format(v)}
            width={60}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="score"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: GOLD }}
            width={28}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(value, name) =>
              name === "score"
                ? [Number(value).toFixed(0), "Score (rückwirkend)"]
                : [priceFormatter.format(Number(value)), "Kurs"]
            }
            labelFormatter={(label) => formatTooltipDate(Number(label))}
            contentStyle={{
              borderRadius: 4,
              borderColor: "#ECEAE3",
              fontSize: 12,
              boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
            }}
          />
          <Area
            yAxisId="price"
            type="linear"
            dataKey="close"
            stroke={NAVY}
            strokeWidth={1.5}
            fill="url(#scoreTimeMachinePriceFill)"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="score"
            type="linear"
            dataKey="score"
            stroke={GOLD}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
